import React, { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Video, VideoOff, X } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Pre-flight device check before joining a huddle.
 *
 * Renders a live camera preview + audio meter, lets the user pick
 * mic/camera devices, and remembers the choice in localStorage under
 * `tovra:huddle:devicePrefs` so subsequent joins skip the modal unless
 * the user explicitly re-opens it from the call bar.
 *
 * Props:
 *  - kind: "voice" | "video"
 *  - onJoin: ({ audio, video, audioDeviceId, videoDeviceId }) => void
 *  - onCancel: () => void
 */
const PREFS_KEY = "tovra:huddle:devicePrefs";

function readPrefs() {
  try { return JSON.parse(localStorage.getItem(PREFS_KEY) || "{}"); }
  catch { return {}; }
}
function writePrefs(p) {
  try { localStorage.setItem(PREFS_KEY, JSON.stringify(p)); } catch { /* ignore */ }
}

export default function HuddlePreflight({ kind = "voice", onJoin, onCancel }) {
  const wantsVideo = kind === "video";
  const savedPrefs = readPrefs();
  const [audioOn, setAudioOn] = useState(savedPrefs.audio !== false);
  const [videoOn, setVideoOn] = useState(wantsVideo && savedPrefs.video !== false);
  const [audioDevices, setAudioDevices] = useState([]);
  const [videoDevices, setVideoDevices] = useState([]);
  const [audioDeviceId, setAudioDeviceId] = useState(savedPrefs.audioDeviceId || "");
  const [videoDeviceId, setVideoDeviceId] = useState(savedPrefs.videoDeviceId || "");
  const [level, setLevel] = useState(0);
  const [permError, setPermError] = useState("");
  const videoEl = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(0);

  // Cleanup helper.
  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: audioOn ? (audioDeviceId ? { deviceId: audioDeviceId } : true) : false,
          video: videoOn ? (videoDeviceId ? { deviceId: videoDeviceId } : true) : false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;

        // Enumerate devices only after permission has been granted so we get labels.
        const devs = await navigator.mediaDevices.enumerateDevices();
        setAudioDevices(devs.filter((d) => d.kind === "audioinput"));
        setVideoDevices(devs.filter((d) => d.kind === "videoinput"));

        if (videoOn && videoEl.current) {
          videoEl.current.srcObject = stream;
          videoEl.current.play?.().catch(() => { /* autoplay blocked; user gesture will unlock */ });
        }

        // Simple audio-level meter using WebAudio.
        if (audioOn) {
          const AC = window.AudioContext || window.webkitAudioContext;
          const ctx = new AC();
          const src = ctx.createMediaStreamSource(stream);
          const analyser = ctx.createAnalyser();
          analyser.fftSize = 512;
          src.connect(analyser);
          const buf = new Uint8Array(analyser.frequencyBinCount);
          const tick = () => {
            analyser.getByteTimeDomainData(buf);
            let peak = 0;
            for (let i = 0; i < buf.length; i++) {
              const v = Math.abs(buf[i] - 128);
              if (v > peak) peak = v;
            }
            setLevel(Math.min(1, peak / 60));
            rafRef.current = requestAnimationFrame(tick);
          };
          tick();
        }
      } catch (e) {
        setPermError(e?.name === "NotAllowedError"
          ? "We need mic/camera permission to preview. Please allow access in your browser."
          : "Could not read from your devices — check that another app isn't using them.");
      }
    })();
    return () => { cancelled = true; stopStream(); };
    // Re-run when the user changes device selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioOn, videoOn, audioDeviceId, videoDeviceId]);

  const join = () => {
    writePrefs({ audio: audioOn, video: videoOn, audioDeviceId, videoDeviceId });
    stopStream();
    onJoin?.({ audio: audioOn, video: videoOn, audioDeviceId, videoDeviceId });
  };

  const cancel = () => { stopStream(); onCancel?.(); };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm"
      data-testid="huddle-preflight"
    >
      <div className="bg-zinc-950 text-white rounded-2xl w-[520px] max-w-[94vw] overflow-hidden shadow-2xl">
        <div className="p-3 flex items-center justify-between border-b border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-400">Get ready</div>
            <div className="font-semibold text-sm">
              {wantsVideo ? "Video huddle" : "Voice huddle"} · check your mic & camera
            </div>
          </div>
          <button
            onClick={cancel}
            aria-label="Cancel"
            data-testid="huddle-preflight-cancel"
            className="p-1.5 rounded hover:bg-white/10"
          ><X className="w-4 h-4"/></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Preview */}
          <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-zinc-900 flex items-center justify-center">
            {videoOn ? (
              <video ref={videoEl} muted playsInline className="w-full h-full object-cover" data-testid="huddle-preflight-preview"/>
            ) : (
              <div className="text-center">
                <VideoOff className="w-10 h-10 text-zinc-500 mx-auto"/>
                <div className="text-xs text-zinc-500 mt-2">Camera off</div>
              </div>
            )}
            {permError && (
              <div className="absolute inset-x-3 bottom-3 text-xs text-red-200 bg-red-900/70 border border-red-500/40 rounded-md px-2 py-1.5">
                {permError}
              </div>
            )}
          </div>

          {/* Mic meter */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1 flex items-center gap-1">
              <Mic className="w-3 h-3"/> Mic level
            </div>
            <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-400 transition-[width]"
                style={{ width: `${Math.round(level * 100)}%` }}
                data-testid="huddle-preflight-meter"
              />
            </div>
          </div>

          {/* Device pickers */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-400">Microphone</label>
              <select
                value={audioDeviceId}
                onChange={(e) => setAudioDeviceId(e.target.value)}
                data-testid="huddle-preflight-mic-select"
                className="mt-1 w-full h-9 bg-zinc-900 border border-white/10 rounded-md text-xs px-2"
              >
                <option value="">System default</option>
                {audioDevices.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label || `Microphone ${d.deviceId.slice(0, 6)}`}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-400">Camera</label>
              <select
                value={videoDeviceId}
                onChange={(e) => setVideoDeviceId(e.target.value)}
                disabled={!wantsVideo}
                data-testid="huddle-preflight-cam-select"
                className="mt-1 w-full h-9 bg-zinc-900 border border-white/10 rounded-md text-xs px-2 disabled:opacity-40"
              >
                <option value="">System default</option>
                {videoDevices.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label || `Camera ${d.deviceId.slice(0, 6)}`}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Toggles */}
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              <button
                onClick={() => setAudioOn((v) => !v)}
                data-testid="huddle-preflight-toggle-mic"
                className={`h-9 px-3 rounded-full text-xs font-medium flex items-center gap-1.5 ${audioOn ? "bg-white/10 hover:bg-white/15" : "bg-red-500/20 text-red-200"}`}
              >
                {audioOn ? <Mic className="w-3.5 h-3.5"/> : <MicOff className="w-3.5 h-3.5"/>}
                {audioOn ? "Mic on" : "Mic off"}
              </button>
              {wantsVideo && (
                <button
                  onClick={() => setVideoOn((v) => !v)}
                  data-testid="huddle-preflight-toggle-cam"
                  className={`h-9 px-3 rounded-full text-xs font-medium flex items-center gap-1.5 ${videoOn ? "bg-white/10 hover:bg-white/15" : "bg-red-500/20 text-red-200"}`}
                >
                  {videoOn ? <Video className="w-3.5 h-3.5"/> : <VideoOff className="w-3.5 h-3.5"/>}
                  {videoOn ? "Camera on" : "Camera off"}
                </button>
              )}
            </div>
            <Button
              onClick={join}
              data-testid="huddle-preflight-join"
              className="h-9 px-5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-medium"
            >Join huddle</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
